import { defineStore } from "pinia";
import { ColorService } from "../services/color.service";
import { CategoryService } from "../services/category.service";
import { SeasonService } from "../services/season.service";
import { TagsService } from "../services/tags.service";
import { VendorService } from "../services/vendor.service";
import { CountryService } from "../services/country.service";
// import { useFilterStore } from "./FilterStore";

const getSelectAllOptionAsDescription = (description) => ({ id: '', description });
const getSelectAllOptionAsText = (text) => ({ id: '', text });
const getSelectAllOptionAsName = (name) => ({ id: '', name });

const appendSelectAllOptionAsText = (allOptionText) => (list) => [getSelectAllOptionAsText(allOptionText), ...list];
const appendSelectAllOptionAsName = (allOptionText) => (list) => [getSelectAllOptionAsName(allOptionText), ...list];

const appendItemId = (prefix) => (item) => ({ ...item, itemId: `${prefix}_${item.id}` });

const createCombinedCategoryArray = (state) => {
  const appendMainCategoryItemId = appendItemId('mc');
  const appendCategoryItemId = appendItemId('c');

  return [...state.mainCategories]
    .map((mcat) => ({ ...mcat, isMainCategory: true }))
    .reduce((acc, mcat) => {
      return [
        ...acc,
        appendMainCategoryItemId(mcat),
        ...state.categoriesByMainCategory(mcat).map(appendCategoryItemId)
      ];
    }, []);
}

const getImageTypes = (trans) =>
  Object.keys(trans('lookup.imageType'))
    .map(key => ({ id: key, name: trans('lookup.imageType')[key] }));


// const initialTypeState = [{ id: '', name: 'Alla bildtyper' }, { id: 1, name: 'Miljöbilder' }, { id: 2, name: 'Produktbilder' }];
const initialState = {
  mainCategories: [],
  categories: [],
  combinedCategories: [],
  colors: [],
  types: [], //[...initialTypeState],
  tags: [],
  seasons: [],
  vendors: [],
  countries: [],
  isLoaded: false,
  isCountriesLoading: false,
};

export const useLookupStore = defineStore("lookup", {
  state: () => ({ ...initialState }),
  getters: {
    combinedCategoriesWithAllOption(state) {
      return state.combinedCategories;
    },

    categoriesByMainCategory(state) {
      const filter = (mainCategory) => (category) => category.main_category_id === mainCategory.id
      return (mainCategory) => state.categories.filter(filter(mainCategory))
    },

    firstCombinedCategoryItem(state) {
      return state.combinedCategoriesWithAllOption[0]; // select all option
    },

    matchingMainCategories(state) {
      return (mainCategoriesId) => state.mainCategories.filter(category => mainCategoriesId.includes(category.id))
    },

    colorsWithoutAllOption(state) {
      return state.colors.filter(color => !!color.id)
    },

    vendorsWithoutAllOption(state) {
      return state.vendors.filter(v => !!v.id);
    },

    vendorsWithAllOption(state) {
      return state.vendors; // [getSelectAllOptionAsName('Alla leverantörer'), ...state.vendors]
    },

    vendorsForSelectedMainCategory(state) {
      return (mainCategoryId) => {
        return state.vendors.filter(vendor => vendor.mainCategories?.includes(mainCategoryId));
      }
    },

    vendorBySlug(state) {
      return (slug) => state.vendors.find(vendor => vendor.slug === slug)
    },

    countryNamesByIds(state) {
      return (countryIds = []) => {
        return state.countries.filter(country => countryIds.includes(country.id)).map(country => country.name).join(', ');
      }
    }
  },
  actions: {
    async load(trans) {
      const promises = [];
      if (this.colors.length === 0) {
        const loadColors = (new ColorService())
          .load()
          .then(appendSelectAllOptionAsText(trans('lookup.allColors')))
          .then(colors => (this.colors = colors));
        promises.push(loadColors);
      }

      if (this.categories.length === 0) {
        const categoryService = new CategoryService();
        promises.push(categoryService.loadMainCategories().then(response => this.mainCategories = response));
        promises.push(categoryService.loadCategories().then(response => this.categories = response));
      }

      if (this.tags.length === 0) {
        const loadTags = (new TagsService()).load().then(tags => {
          this.tags = tags.filter(tag => Boolean(tag?.name));
        });
        promises.push(loadTags);
      }

      if (this.vendors.length === 0) {
        const loadVendors = (new VendorService())
          .loadVendorsWithImages()
          .then(appendSelectAllOptionAsName(trans('lookup.allCompanies')))
          .then(vendors => (this.vendors = vendors));
        promises.push(loadVendors);
      }

      this.types = getImageTypes(trans);

      if (promises.length > 0) {
        await Promise.all(promises).finally(() => {
          this.combinedCategories = [
            getSelectAllOptionAsDescription(trans('lookup.allCategories')),
            ...createCombinedCategoryArray(this)
          ];
          this.isLoaded = true;

          // const filterStore = useFilterStore();
          // console.log('LookupStore: load -> filterStore reset');
          // filterStore.reset();
        })
      }
    },

    async loadSeasons() {
      if (this.seasons.length > 0) {
        return;
      }
      const seasons = await (new SeasonService()).load();

      // flytta "säsongsoberoende" till början
      const seasonIndependentIndex = seasons.findIndex((item) => item.id === 1);
      seasons.unshift(seasons.splice(seasonIndependentIndex, 1)[0]);

      this.seasons = seasons
    },

    async loadCountries() {
      if (this.countries.length > 0 || this.isCountriesLoading) {
        return;
      }
      this.isCountriesLoading = true;

      const countries = await (new CountryService()).load();
      this.countries = countries;
      this.isCountriesLoading = false;
    },
  }
})